/**
 * Utilities for building summary request constraints from Charts-2 context
 */

import {
  type BreakdownSelection,
  customBreakdownFieldAt,
  getBreakdownLabel,
  isCustomBreakdown,
  isDatasetBreakdown,
  isStandardBreakdown,
} from 'features/charts-v2/components/breakdown-selection-utils'
import { STACKING_OVERFLOW_LABEL } from 'features/charts-v2/constants'
import {
  Dimension,
  getCustomDimension,
  isJSONDimension,
  type JSONDimension,
  type SelectedFilterOption,
} from 'features/charts-v2/types'
// bully ignore[shared-imports-features: rule's applies predicate is always_ts_file (tools/bully/policy/src/rules/rules/shared_imports_features/mod.rs:19), not scoped to configured shared_layer_paths — same-feature import (charts-v2/utils importing charts-v2/types). follow-up: .agents/follow-ups/bully-typescript-false-positive-rules.md]
import type { Breakdown } from 'features/charts-v2/types/filter-hash/v2'
import { unfoldRatingOnlyFilter } from 'features/charts-v2/utils/fold-rating-only-filter'
import { unformatBreakdownCategoryValue } from 'features/charts-v2/utils/format-breakdown-value'
import {
  findRidgelineBin,
  getSentimentBinEdges,
  isRidgelineMetric,
} from 'features/charts-v2/utils/ridgeline-bins'
import {
  type AppliedFilterOptionRange,
  type AppliedFilterOptionSelect,
  type FilterOption,
  type FilterOptionSelect,
} from 'features/filters/types'
import {
  ALIAS_FOR_NULL_VALUES,
  isNullValueAlias,
} from 'src/constants/filter-values'

/**
 * Maps standard breakdown names to their corresponding Dimension values
 */
export function mapBreakdownToDimension(
  breakdown: BreakdownSelection,
): Dimension | JSONDimension | null {
  if (!breakdown) return null

  if (isStandardBreakdown(breakdown)) {
    const mapping: Record<string, Dimension> = {
      theme: Dimension.THEME,
      brand: Dimension.PRODUCT_BRAND,
      product: Dimension.PRODUCT_HIERARCHY,
      source: Dimension.SOURCE_NAME,
      sentiment: Dimension.POST_TEXT_SENTIMENT,
      rating: Dimension.POST_SCORE,
      country: Dimension.POST_COUNTRY,
      'parent theme': Dimension.PARENT_THEME,
      source_type: Dimension.SOURCE_NAME,
      promotional: Dimension.POST_PROMOTION,
      dataset: Dimension.SOURCE_PROJECT_ID,
    }
    return mapping[breakdown.id] || null
  }

  // For custom breakdowns, create a JSONDimension.
  // - Use getCustomDimension(model) for the base field: breakdown.field is a
  //   bare key like "source_dataset" so substring checks are unreliable.
  // - keys[0] must be the raw field name (the literal JSON property the
  //   backend's ClickHouse accessor indexes), never the display alias.
  if (isCustomBreakdown(breakdown)) {
    return {
      field: getCustomDimension(breakdown.model),
      keys: [breakdown.field],
    }
  }

  return null
}

/**
 * The wire Dimension column string each groupable breakdown is keyed under
 * in the backend's per-axis category_mapping. The backend groups themes
 * under the CLUSTER_TOPIC column (not THEME), so the theme breakdown maps
 * to "cluster_topic" here even though mapBreakdownToDimension('theme')
 * returns Dimension.THEME for constraint emission.
 */
const CATEGORY_MAPPING_AXIS_KEY: Record<string, string> = {
  brand: Dimension.PRODUCT_BRAND,
  product: Dimension.PRODUCT_HIERARCHY,
  theme: Dimension.CLUSTER_TOPIC,
  'parent theme': Dimension.CLUSTER_TOPIC,
}

/**
 * Resolves the per-axis category_mapping for a single grouped breakdown to
 * its flat { bucketLabel: [children] } dict. Returns {} when the breakdown
 * is not a known groupable axis or the axis is absent from the mapping.
 */
export const resolveCategoryMappingAxis = (
  categoryMapping: Record<string, Record<string, string[]>>,
  breakdown: BreakdownSelection,
): Record<string, string[]> => {
  if (!breakdown || !isStandardBreakdown(breakdown)) {
    return {}
  }
  const axisKey = CATEGORY_MAPPING_AXIS_KEY[breakdown.id]
  if (!axisKey) {
    return {}
  }
  return categoryMapping[axisKey] ?? {}
}

/**
 * Resolves the real child values for a grouped bucket label.
 *
 * category_mapping is keyed per axis by the wire Dimension column string
 * (e.g. "product_brand"); each axis maps a bucket label ("My Brands" /
 * "Competitor Brands") to its real child values. Returns [] when the axis
 * or the bucket is absent — the caller must NOT fall back to an equality
 * constraint on the synthetic bucket label (PROD-3451).
 */
const expandGroupedBucket = (
  categoryMapping: Record<string, Record<string, string[]>>,
  dimension: Dimension | JSONDimension,
  bucketLabel: string,
): string[] => {
  if (isJSONDimension(dimension)) {
    return []
  }
  return categoryMapping[dimension]?.[bucketLabel] ?? []
}

/**
 * Maps a breakdown's resolved Dimension to the friendly filter-field name the
 * backend's chart transpiler recognizes on `SelectedFilterOptionV2.field`
 * (`FIELD_TO_DIMENSION` in `chart_transpiler/emit/filters.py`). This is a
 * DIFFERENT table from the display-only badge mapping below —
 * `Dimension.SOURCE_PROJECT_ID` must map to `'dataset'` here so drilldowns carry
 * the integer project id expected by the analytics table.
 * Getting this table wrong silently drops the filter or routes it to the wrong
 * column — see charts-v2/AGENTS.md.
 */
const DIMENSION_TO_DRILLDOWN_FILTER_FIELD: Partial<Record<Dimension, string>> =
  {
    [Dimension.THEME]: 'theme',
    [Dimension.CLUSTER_TOPIC]: 'theme',
    [Dimension.PARENT_THEME]: 'parent_theme',
    [Dimension.PRODUCT_BRAND]: 'brand',
    [Dimension.PRODUCT_GROUP]: 'product_group',
    [Dimension.PRODUCT_HIERARCHY]: 'product_hierarchy',
    [Dimension.SOURCE_NAME]: 'source_type',
    [Dimension.POST_TEXT_SENTIMENT]: 'sentiment',
    [Dimension.POST_AVG_SENTIMENT]: 'post_sentiment',
    [Dimension.POST_SCORE]: 'score',
    [Dimension.POST_COUNTRY]: 'country',
    [Dimension.POST_PROMOTION]: 'promotion',
    [Dimension.SOURCE_PROJECT_ID]: 'dataset',
  }

/**
 * Resolves a breakdown's Dimension (+ custom JSONDimension) to the wire filter
 * field for a drill-down SelectedFilterOption. Custom breakdowns resolve back
 * to their filter option's canonical field via the same key/alias lookup
 * `convertFilterOptionsToCriteria` uses for custom fields.
 */
const dimensionToDrilldownFilterField = (
  dimension: Dimension | JSONDimension,
  filterOptions: FilterOption[] | undefined,
): string | null => {
  if (isJSONDimension(dimension)) {
    const key = dimension.keys[0] ?? ''
    const option = filterOptions?.find(
      (o) => o.field === key || o.alias === key,
    )
    return option?.field ?? key
  }
  return DIMENSION_TO_DRILLDOWN_FILTER_FIELD[dimension] ?? null
}

/**
 * Normalizes a breakdown's display value into the wire value the backend
 * expects: FE-fabricated null aliases ("Unknown", "N/A", "" — see
 * `isNullValueAlias`) round-trip as the backend's canonical null token
 * (`ALIAS_FOR_NULL_VALUES`, which `emit_generic_filter` et al. recognize
 * literally). Sending the raw fabricated label instead would silently match
 * zero rows — see charts-v2/AGENTS.md's "Unknown is FE-fabricated" trap.
 */
const normalizeDrilldownValue = (value: string): string =>
  isNullValueAlias(value) ? ALIAS_FOR_NULL_VALUES : value

/**
 * Resolves a dataset breakdown value (display title, proj_uuid, cluster_uuid,
 * or identifier) to a value the BE can apply as a SOURCE_PROJECT_ID constraint.
 *
 * Returns the integer project_id when filterOptions has it (preferred — types
 * cleanly with the Int32 column). Falls back to the project UUID (read from
 * cluster_uuid, which on the proj_uuid filter option *is* the project UUID,
 * dual-written for legacy compat) so a stale filter-options cache that
 * predates the project_id field still produces a working constraint — the BE
 * translates the UUID server-side via translate_proj_uuid_constraints.
 *
 * Returns null only when the value matches no row at all.
 */
const resolveDatasetValueToProjectId = (
  value: string,
  filterOptions: FilterOption[] | undefined,
): number | string | null => {
  const projUuidOption = filterOptions?.find(
    (opt) => opt.field === 'proj_uuid',
  ) as FilterOptionSelect | undefined
  if (!projUuidOption) return null
  for (const v of projUuidOption.values) {
    const matches =
      value === v.proj_uuid ||
      value === v.cluster_uuid ||
      value === v.identifiers?.[0] ||
      value === v.value
    if (!matches) continue
    if (typeof v.project_id === 'number') return v.project_id
    return v.proj_uuid ?? v.cluster_uuid ?? null
  }
  return null
}

/**
 * Builds a single-value SelectedFilterOption for a breakdown drill-down.
 */
const drilldownFilterOption = (
  field: string,
  values: (string | number)[],
): SelectedFilterOption => ({
  field,
  operator: 'is',
  values: values.map((v) => normalizeDrilldownValue(String(v))),
})

/**
 * Resolves the SelectedFilterOption for a single breakdown level's clicked
 * value, or null when the value can't be resolved to a filter (e.g. a
 * grouped bucket with no category_mapping entry, or a dataset title with no
 * matching filter option — never fall back to filtering on the display label
 * itself, which is not a real column value; see charts-v2/AGENTS.md).
 */
const resolveDrilldownFilterOption = (
  breakdown: Breakdown,
  value: string,
  filterOptions: FilterOption[] | undefined,
  categoryMapping: Record<string, Record<string, string[]>> | null | undefined,
  clickedValueIsGroupedLeaf: boolean,
): SelectedFilterOption | null => {
  const dimension = mapBreakdownToDimension(breakdown)
  if (!dimension) return null

  const field = dimensionToDrilldownFilterField(dimension, filterOptions)
  if (!field) return null

  // Grouped product/brand/theme bar: the bar label ("My Products",
  // "Competitor Brands", "Product Effectiveness", etc.) is a synthetic
  // bucket name, not a real column value. For grouped theme specifically,
  // the parent bucket is assembled server-side in Python
  // (group_themes_with_rollup_override) by summing child CLUSTER_TOPIC rows
  // under their shared "Parent: " prefix — the parent label itself never
  // appears literally in CLUSTER_GROUP or CLUSTER_TOPIC, so emit_theme_filter's
  // exact COALESCE(CLUSTER_GROUP, CLUSTER_TOPIC) match returns zero rows for
  // it (PROD bug: grouped-theme drill-down returned 0 records/sentences).
  // Expand via category_mapping so the BE query matches the correct child
  // values instead. Must be checked before the theme passthrough below to
  // avoid TypeScript narrowing away GroupableBreakdown.
  if (
    isStandardBreakdown(breakdown) &&
    (breakdown.id === 'brand' ||
      breakdown.id === 'product' ||
      breakdown.id === 'theme') &&
    breakdown.grouped
  ) {
    // Grouped CHILD/leaf row: `value` is a real column value (e.g. "Ball
    // Park White Hot Dog Buns", or a full "Parent: Child" theme name), not a
    // synthetic bucket label.
    if (clickedValueIsGroupedLeaf) {
      return drilldownFilterOption(field, [value])
    }
    if (!categoryMapping) return null
    // category_mapping is keyed by the wire Dimension column the BE grouped
    // on, which for theme is CLUSTER_TOPIC — NOT Dimension.THEME (the
    // filter-field dimension resolved above). See CATEGORY_MAPPING_AXIS_KEY.
    const expansionDimension =
      breakdown.id === 'theme' ? Dimension.CLUSTER_TOPIC : dimension
    const children = expandGroupedBucket(
      categoryMapping,
      expansionDimension,
      value,
    )
    // Only emit a filter when the bucket expands to real children. Never
    // fall back to an EQ on the synthetic bucket label (PROD-3451).
    if (!children.length) return null
    return {
      ...drilldownFilterOption(field, children),
      groupBucket: {
        field:
          breakdown.id === 'theme' ? 'parent_theme' : `${breakdown.id}_group`,
        value,
      },
    }
  }

  if (isDatasetBreakdown(breakdown)) {
    // SOURCE_PROJECT_ID is an Int32 project id; the chart axis shows display
    // titles. Resolve via filterOptions or drop the filter.
    const projectId = resolveDatasetValueToProjectId(value, filterOptions)
    if (projectId === null) return null
    return drilldownFilterOption(field, [projectId])
  }

  // Sentence-level sentiment stacking keys its categories lowercase
  // ('positive'/'neutral'/'negative' — see dataTransformationService's
  // sentimentCategories); the backend's emit_sentiment_filter matches
  // case-sensitively on "Positive"/"Neutral"/"Negative", so a raw lowercase
  // value silently drops the filter (returns null, emits no constraint).
  // Title-case here so the two agree.
  if (dimension === Dimension.POST_TEXT_SENTIMENT) {
    const titleCased =
      value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
    return drilldownFilterOption(field, [titleCased])
  }

  // Theme (name or UUID), promotion/source-type display label, and rating
  // value all pass straight through as the raw value — the backend's
  // emit_single_filter (emit_theme_filter / emit_promotion_filter /
  // emit_generic_filter's SOURCE_NAME_FIELDS / emit_score_filter) applies
  // the SAME normalization the chart itself relies on, so summarize and the
  // chart agree by construction.
  return drilldownFilterOption(field, [value])
}

/**
 * Builds drill-down filter options from a clicked chart element.
 */
export interface ClickedElement {
  name: string
  value: number
  stackingBreakdown?: string
}

export interface SummaryConstraintOptions {
  dateRange: {
    startDate: Date | null
    endDate: Date | null
  }
  clickedElement: ClickedElement
  primaryBreakdown: BreakdownSelection | null
  stackingBreakdown?: BreakdownSelection | null
  filterOptions?: FilterOption[]
  categoryMapping?: Record<string, Record<string, string[]>> | null
}

export const buildDrilldownFilterOptions = (
  options: SummaryConstraintOptions,
): SelectedFilterOption[] => {
  const filters: SelectedFilterOption[] = []

  if (options.primaryBreakdown && options.clickedElement.name) {
    const resolved = resolveDrilldownFilterOption(
      options.primaryBreakdown,
      options.clickedElement.name,
      options.filterOptions,
      options.categoryMapping,
      false,
    )
    if (resolved) filters.push(resolved)
  }

  if (options.stackingBreakdown && options.clickedElement.stackingBreakdown) {
    const resolved = resolveDrilldownFilterOption(
      options.stackingBreakdown,
      options.clickedElement.stackingBreakdown,
      options.filterOptions,
      options.categoryMapping,
      false,
    )
    if (resolved) filters.push(resolved)
  }

  return filters
}

/**
 * Options for building drill-down filters from multiple breakdown levels
 */
export interface MultiBreakdownConstraintOptions {
  dateRange: {
    startDate: Date | null
    endDate: Date | null
  }
  breakdownPairs: Array<{
    breakdown: BreakdownSelection
    value: string
  }>
  filterOptions?: FilterOption[]
  categoryMapping?: Record<string, Record<string, string[]>> | null
  /** True when the clicked row is a grouped-breakdown child/leaf whose value is a real
   *  dimension value (e.g. "Ball Park White Hot Dog Buns"), not a synthetic bucket label
   *  (e.g. "My Products"). When set, the grouped product/brand branch emits a direct
   *  filter instead of expanding the value via category_mapping (which would return []
   *  for a leaf and silently drop the filter — the root cause of the child-row
   *  summarize bug). */
  clickedValueIsGroupedLeaf?: boolean
}

/**
 * Builds drill-down filter options from multiple breakdown levels (for
 * tables with N breakdowns). Generalizes buildDrilldownFilterOptions to an
 * arbitrary number of breakdowns.
 */
export const buildMultiBreakdownDrilldownFilterOptions = (
  options: MultiBreakdownConstraintOptions,
): SelectedFilterOption[] => {
  const filters: SelectedFilterOption[] = []

  options.breakdownPairs.forEach(({ breakdown, value }) => {
    if (!breakdown || !value) return
    const resolved = resolveDrilldownFilterOption(
      breakdown,
      value,
      options.filterOptions,
      options.categoryMapping,
      options.clickedValueIsGroupedLeaf ?? false,
    )
    if (resolved) filters.push(resolved)
  })

  return filters
}

/** What a clicked ridgeline bucket adds to a summary: the bucket's filters and
 *  the title segment that names it (joined to the title with " > "). */
export interface RidgelineBucketSummary {
  filters: SelectedFilterOption[]
  titleSuffix: string
}

const NO_RIDGELINE_BUCKET: RidgelineBucketSummary = {
  filters: [],
  titleSuffix: '',
}

/**
 * Resolves the x-bucket a ridgeline click landed on (`ridgelineMetric` +
 * `ridgelineBin`, set by RidgelineChartView) into the filter that narrows a
 * summary to it. Empty for any other chart's click payload.
 *
 * A rating bucket is an equality on the star. A sentiment bucket is the
 * half-open score range the backend's SENTIMENT_BIN bins by, so its edges are
 * sent verbatim: a `0` bound is a real bound, never "unset".
 */
export const buildRidgelineBucketSummary = (
  payload: Record<string, unknown> | undefined,
): RidgelineBucketSummary => {
  const metric = payload?.['ridgelineMetric']
  const key = payload?.['ridgelineBin']
  if (!isRidgelineMetric(metric) || typeof key !== 'number') {
    return NO_RIDGELINE_BUCKET
  }
  const bin = findRidgelineBin(metric, key)
  if (!bin) return NO_RIDGELINE_BUCKET

  if (metric === 'rating') {
    return {
      filters: [{ field: 'score', operator: 'is', values: [String(bin.key)] }],
      titleSuffix: `Rating: ${bin.label}`,
    }
  }
  return {
    filters: [
      {
        field: 'sentiment',
        operator: 'is',
        values: [],
        ...getSentimentBinEdges(bin.key),
      },
    ],
    titleSuffix: `Sentiment: ${bin.label}`,
  }
}

/** What a clicked Sankey ribbon or node adds to a summary: the filters that
 *  narrow it to the flow, and a title naming it ("Brand: X → Theme: Y"). */
export interface SankeySummary {
  filters: SelectedFilterOption[]
  title: string
}

const NO_SANKEY_SUMMARY: SankeySummary = { filters: [], title: '' }

interface SankeyEnd {
  breakdownIndex: number
  level: 'group' | 'value'
  /** Null for the "Other" node, which stands for many values. */
  value: string | null
}

const readSankeyEnd = (
  payload: Record<string, unknown> | undefined,
  end: 'Source' | 'Target',
): SankeyEnd | null => {
  const breakdownIndex = payload?.[`sankey${end}Breakdown`]
  const level = payload?.[`sankey${end}Level`]
  const value = payload?.[`sankey${end}Value`]
  if (
    typeof breakdownIndex !== 'number' ||
    (level !== 'group' && level !== 'value')
  ) {
    return null
  }
  return {
    breakdownIndex,
    level,
    value: typeof value === 'string' ? value : null,
  }
}

/**
 * Resolves a Sankey click (the `sankey*` fields SankeyChartView sets on the
 * click payload) into the summary it opens: a ribbon narrows to both ends, a
 * node to one. Empty for any other chart's click payload.
 *
 * A group node expands to its members through `categoryMapping`; a member node
 * is a plain equality. "Other" contributes no filter. A ribbon from a group to
 * its own members filters on the member alone, since the group is implied.
 */
export const buildSankeySummary = (
  payload: Record<string, unknown> | undefined,
  selectedBreakdowns: readonly BreakdownSelection[],
  categoryMapping: Record<string, Record<string, string[]>> | null | undefined,
  filterOptions?: FilterOption[],
): SankeySummary => {
  const source = readSankeyEnd(payload, 'Source')
  if (!source) return NO_SANKEY_SUMMARY
  const target = readSankeyEnd(payload, 'Target')
  const ends = target ? [source, target] : [source]

  const sameBreakdown =
    target !== null && target.breakdownIndex === source.breakdownIndex
  const filters: SelectedFilterOption[] = []
  const titleParts: string[] = []
  ends.forEach((end) => {
    const breakdown = selectedBreakdowns[end.breakdownIndex]
    if (!breakdown) return
    titleParts.push(
      `${getBreakdownLabel(breakdown)}: ${end.value ?? STACKING_OVERFLOW_LABEL}`,
    )
    if (end.value === null || (sameBreakdown && end.level === 'group')) return
    const resolved = resolveDrilldownFilterOption(
      breakdown,
      unformatBreakdownCategoryValue(
        customBreakdownFieldAt(selectedBreakdowns, end.breakdownIndex),
        end.value,
      ),
      filterOptions,
      categoryMapping,
      end.level === 'value',
    )
    if (resolved) filters.push(resolved)
  })
  return { filters, title: titleParts.join(' → ') }
}

/**
 * Converts SelectedFilterOption[] to AppliedFilterOption[].
 *
 * The result renders the summary-panel filter badges. For `/post/v2/summarize`,
 * `/reviews`, and `/stats` this is FE-display-only — those endpoints send `panel`
 * directly (the same representation /charts/chart transpiles) and derive their
 * own filter set from `panel.filters`. `/summary-panel` (the theme-summary LLM
 * endpoint, via `buildSummaryCriteria` below) has no `panel` leg, so for that
 * caller this criteria array IS the filter source, not just a badge.
 */
export const convertFilterOptionsToCriteria = (
  filters: SelectedFilterOption[],
  filterOptions?: FilterOption[],
): Array<AppliedFilterOptionSelect | AppliedFilterOptionRange> => {
  const criteria: Array<AppliedFilterOptionSelect | AppliedFilterOptionRange> =
    []

  // A folded rating-only `valueType` has no range for the branch below.
  unfoldRatingOnlyFilter(filters).forEach((filter) => {
    // Handle score/rating filters with range
    if (
      (filter.field === 'score' || filter.field === 'post_score') &&
      (filter.min !== undefined || filter.max !== undefined)
    ) {
      criteria.push({
        field: filter.field,
        min: filter.min ?? 0,
        max: filter.max ?? 0,
        value_type: filter.valueType || 0,
      })
      return
    }

    // A sentiment bucket is a numeric range with no values. Both bounds are
    // kept as given: 0 is a valid edge, so no `?? 0` / truthiness defaulting.
    if (
      filter.field === 'sentiment' &&
      filter.min !== undefined &&
      filter.max !== undefined
    ) {
      criteria.push({
        field: filter.field,
        min: filter.min,
        max: filter.max,
        value_type: 0,
      })
      return
    }

    // Handle regular select filters
    if (filter.values.length > 0) {
      let resolvedValues = filter.values
      if (filter.field === 'proj_uuid') {
        const projUuidOption = filterOptions?.find(
          (opt) => opt.field === 'proj_uuid',
        ) as FilterOptionSelect | undefined
        if (projUuidOption) {
          // Emit the project UUID (not the display title) so the
          // /summary-panel criterion resolves against project.uuid.
          // The badge still renders the dataset name via getBadgeLabels.
          resolvedValues = filter.values.map((v) => {
            const match = projUuidOption.values.find(
              (val) =>
                v === val.proj_uuid ||
                v === val.cluster_uuid ||
                v === val.identifiers?.[0] ||
                v === val.value,
            )
            return match?.proj_uuid ?? match?.cluster_uuid ?? v
          })
        }
      }
      criteria.push({
        field: filter.field,
        values: resolvedValues,
      })
    }
  })

  return criteria
}

/**
 * Assembles the `/summary-panel` payload's `criteria`: leads with the tile's
 * resolved date window, then drilldown filters, then sidebar panel filters,
 * de-duplicated by field (first occurrence wins).
 *
 * NOT display-only: `/summary-panel` (the theme-summary LLM endpoint) has no
 * `panel` leg and derives its ENTIRE filter set — date included — from this
 * array. Dropping `create_time` here silently summarizes all time instead of
 * the tile's date range.
 */
export const buildSummaryCriteria = (
  dateRange: { startDate: number; endDate: number },
  drilldownFilters: SelectedFilterOption[],
  panelFilters: SelectedFilterOption[],
  filterOptions?: FilterOption[],
): Array<AppliedFilterOptionSelect | AppliedFilterOptionRange> => {
  const seenFields = new Set<string>()
  return [
    {
      field: 'create_time',
      min: dateRange.startDate,
      max: dateRange.endDate,
      value_type: 0,
    },
    ...convertFilterOptionsToCriteria(drilldownFilters, filterOptions),
    ...convertFilterOptionsToCriteria(panelFilters, filterOptions),
  ].filter((c) => {
    if (seenFields.has(c.field)) return false
    seenFields.add(c.field)
    return true
  })
}
